/**
 * Whether the open Forms tab is holding work that has not been applied yet.
 *
 * WHY A MODULE-LOCAL FLAG. The designer-live hybrid refresh has to know whether a tool is BUSY
 * before it applies a remote change: a busy tool gets the reload DEFERRED (toast + toolbar badge)
 * until it goes idle. For workflow screens that question was still being answered by the RETIRED
 * wizard modal's open-flag, so the Forms tab — which is where the work actually happens now —
 * counted as idle no matter how much unapplied work it held. A remote apply could then reload the
 * project underneath it, and because a persisted step↔screen binding outranks a local draft, the
 * user's draft silently stopped being the thing on screen.
 *
 * The flag lives here rather than in the shared screen-design state for the same reason
 * `process-editor-tab-bus` and `forms-interactions-focus-bus` do: this is pure-UI bookkeeping,
 * and the screen-design model is a Top-risk schema that should not grow fields for it.
 *
 * Only one Forms tab is mounted at a time (the process editor keeps a single instance), so a
 * single flag is the whole state. It is set from the tab's render-time pending count and cleared
 * on unmount — a tab that is gone can hold nothing.
 */

let hasUnappliedWork: boolean = false;

/**
 * Publish the tab's dirty state. Idempotent, and when it transitions to CLEAN it tells
 * designer-live that the tool went idle, so a reload deferred while the user was mid-edit lands
 * immediately instead of waiting for the next unrelated event.
 */
export const setFormsTabDirty = (isDirty: boolean, onBecameIdle?: () => void): void => {
  const wasDirty: boolean = hasUnappliedWork;
  hasUnappliedWork = isDirty;
  if (wasDirty && !isDirty) {
    onBecameIdle?.();
  }
};

/** Whether the open Forms tab holds unapplied work. Read by the designer-live refresh gate. */
export const isFormsTabDirty = (): boolean => hasUnappliedWork;

/**
 * Forget any dirty state, without firing the idle callback.
 *
 * For unmount: the tab is gone, so it holds nothing and there is no "became idle" moment to
 * report — the reload the gate was deferring is applied by the caller's own teardown path.
 */
export const resetFormsTabDirty = (): void => {
  hasUnappliedWork = false;
};
